import React, { useContext, useEffect, useState } from "react";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import AppSettings from "../../../../Settings/AppSettings";
import { CategoryContext } from "../CategoryContext";
import { arrayMove, List } from "react-movable";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { AppContext } from "../../../../Context/AppContext";
import ViewMoreButton from "../../../Common/components/ViewMoreButton";
import LoadingSuccessErrorAlert from "./LoadingSuccessErrorAlert";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Button from "Components/DesignSystem/Button/Button";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Drag_n_drop } from "Components/DesignSystem/Iconography/Icons";

// number of attributes to show at a time
const _max_items_show = 15
/**
 * PDW Attribute Order — on the design system since the category module's
 * Phase 5. Every service call, payload shape ({ id, column_order }), permission
 * check, JSON-compare dirty gate, the view-more slice and the stale-closure
 * 5000ms timer (pinned as a § 7 must-not-regress) are the legacy logic
 * verbatim; only the rendering changed: DS Accordion (controlled, § 192 actions
 * slot), react-movable KEPT with rows restyled on role tokens, § 99 strip.
 */
const PdwAttributeOrder = () => {
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const { editingItem } = useContext(CategoryContext);
  const [loading, setLoading] = useState({ status: "", message: "", });
  const [list, setList] = useState(null);
  const [EditPage, setEditPage] = useState(false);
  const [collapse, setCollapse] = useState(false);
  const appData = useContext(AppContext);
  const [pdwAttributeOrder, setPdwAttributeOrder] = useState(null);
  const [viewMore, setViewMore] = useState(false);

  useEffect(() => {
    setPdwAttributeOrder(null);
    setLoading({ status: "", message: "", });
  }, [editingItem.id]);

  useEffect(() => {
    if (list === null && collapse) {
      load_associated_pdw_attributes();
    }
  }, [collapse]);

  useEffect(() => {
    if (EditPage) {
      load_associated_pdw_attributes();
    }
  }, [EditPage]);

  async function load_associated_pdw_attributes() {
    setLoading({ status: "Loading", message: "Loading Pdw Attribute Order" });
    const res = await ServiceWrapper.doGet(
      `${api_base_url}category/${editingItem.id}/pdwattributes/list`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      setLoading({ status: "Success", message: undefined });
      setPdwAttributeOrder(res.data.data);
      setList(res.data.data);
    } else {
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }

  async function handle_save() {
    setLoading({ status: "Loading", message: "Saving new order for PDW attributes", });
    const newOrder = list.map((item, index) => ({ id: item.id, column_order: `${index + 1}`, }));
    const res = await ServiceWrapper.doPost(
      `${api_base_url}category/${editingItem.id}/pdwattributes/edit`,
      newOrder,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      if (res.data.done) {
        setLoading({ status: "Success", message: res.data.message, });
        setEditPage(false);
        load_associated_pdw_attributes();
      } else {
        setLoading({ status: "Error", message: res.data.message, });
      }
    } else {
      setLoading({ status: "Error", message: "Error while saving, Please try after some time.", });
    }
    setTimeout(() => {
      if (loading.status !== "Success")
        setLoading({ status: "", message: "", });
    }, 5000);
  }

  function handle_cancel() {
    setEditPage(false);
    setList(pdwAttributeOrder);
  }

  /* Header-row actions (§ 192): the legacy floated these OVER the header with
     zIndex + marginTop:-34px; same render conditions, same handlers. */
  const headerActions =
    collapse && loading.status === "Success" ? (
      !EditPage ? (
        pdwAttributeOrder && pdwAttributeOrder.length > 1 ? (
          <Button
            variant="subtle"
            size="sm"
            data-testid="category-pdw-attribute-order-edit-btn"
            onClick={() => {
              if (appData.pm.checkPermission("gmscategory_update")) {
                setEditPage(true);
              } else {
                toast.info(
                  "You don't have permission to edit PDW Attribute order"
                );
              }
            }}
          >
            Edit
          </Button>
        ) : undefined
      ) : (
        <>
          <Button
            variant="subtle"
            size="sm"
            data-testid="category-pdw-attribute-order-reset-btn"
            onClick={() => setList(pdwAttributeOrder)}
            disabled={
              JSON.stringify(pdwAttributeOrder) === JSON.stringify(list)
            }
          >
            Reset
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid="category-pdw-attribute-order-cancel-btn"
            onClick={() => {
              handle_cancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="category-pdw-attribute-order-save-btn"
            disabled={
              JSON.stringify(pdwAttributeOrder) === JSON.stringify(list)
            }
            onClick={() => handle_save()}
          >
            Save
          </Button>
        </>
      )
    ) : undefined;

  return (
    <Accordion
      title={`${EditPage ? "Update " : ""}PDW Attribute Order`}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      actions={headerActions}
      data-testid="components-pdwattributeorder-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        <LoadingSuccessErrorAlert
          loading={loading}
          data-testid="category-pdw-attribute-order-status"
        />

        {loading.status === "Success" && (
          <>
            {EditPage ? (
              <div className="tw-pt-3">
                <Typography
                  variant="sub-title-label"
                  as="h3"
                  className="tw-mb-2 tw-text-ink-secondary"
                >
                  PDW Attribute Order Ranking
                </Typography>
                <table className="tw-w-full">
                  <List
                    values={list}
                    onChange={({ oldIndex, newIndex }) =>
                      setList(arrayMove(list, oldIndex, newIndex))
                    }
                    renderList={({ children, props }) => (
                      <tbody {...props}>{children}</tbody>
                    )}
                    renderItem={({ value, props }) => {
                      const { key, ...restProps } = props;
                      return (
                        <tr key={key} className="tw-bg-surface">
                          <td className="tw-py-1" {...restProps}>
                            <span className="tw-flex tw-items-center tw-gap-2 tw-rounded-ctl tw-bg-surface-sunken tw-px-2 tw-py-1.5 tw-text-body-primary tw-text-ink tw-cursor-grab">
                              <Drag_n_drop
                                aria-hidden="true"
                                className="tw-shrink-0 tw-text-ink-tertiary"
                              />
                              {key !== undefined && `${key + 1}. `} {value.name}
                            </span>
                          </td>
                        </tr>
                      );
                    }}
                    transitionDuration={100}
                    lockVertically={true}
                  />
                </table>
              </div>
            ) : (
              <div className="tw-pt-3">
                {pdwAttributeOrder && pdwAttributeOrder.length > 0 ? (
                  <div className="tw-space-y-1">
                    {pdwAttributeOrder.slice(0,
                      (viewMore)
                      ? pdwAttributeOrder.length : Math.min(_max_items_show, pdwAttributeOrder.length)
                      ).map((item, index) => {
                      let pdwName = `${index + 1}  ${item.name}`;
                      return (
                        <Typography
                          key={index}
                          variant="body-primary"
                          className="tw-text-ink-secondary"
                        >
                          {pdwName}
                        </Typography>
                      );
                    })}
                    <ViewMoreButton
                      state={viewMore}
                      handleClick={() => setViewMore(!viewMore)}
                      condition={pdwAttributeOrder?.length > _max_items_show}
                    />
                  </div>
                ) : (
                  <EmptyState size="sm" title="PDW attributes Not Found" />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Accordion>
  );
};
export default PdwAttributeOrder;
